// apps/web/src/components/ui/FormField.tsx
//
// Accessible form field (label + input + help/error text). Every
// input has an associated <label> (not placeholder-as-label), the
// error/help is wired to the input via aria-describedby, and
// errors are announced via an aria-live="polite" region (not
// just color change — phase-1-5 §3).

import type { InputHTMLAttributes, ReactNode } from 'react';
import { useId } from 'react';

export interface FormFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'children'> {
  label: string;
  helpText?: string;
  errorText?: string;
  optional?: boolean;
  rightAdornment?: ReactNode;
}

export function FormField({
  label,
  helpText,
  errorText,
  optional = false,
  required,
  rightAdornment,
  id,
  className,
  type = 'text',
  ...rest
}: FormFieldProps): JSX.Element {
  const reactId = useId();
  const inputId = id ?? `f-${reactId}`;
  const helpId = helpText ? `${inputId}-help` : undefined;
  const errorId = errorText ? `${inputId}-error` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={className}>
      <label htmlFor={inputId} className="block text-sm font-medium text-gray-800 dark:text-gray-200">
        {label}
        {optional ? <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">(optional)</span> : null}
      </label>
      <div className="relative mt-1">
        <input
          id={inputId}
          type={type}
          required={required}
          aria-invalid={errorText ? true : undefined}
          aria-describedby={describedBy}
          className={[
            'block w-full rounded border bg-white px-3 py-2 text-sm text-gray-900 shadow-sm',
            'border-gray-300 placeholder-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500',
            'disabled:bg-gray-50 disabled:text-gray-500',
            'dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-500',
            errorText ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : '',
            rightAdornment ? 'pr-10' : '',
          ].join(' ')}
          {...rest}
        />
        {rightAdornment ? (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-sm text-gray-500">
            {rightAdornment}
          </div>
        ) : null}
      </div>
      {helpText ? (
        <p id={helpId} className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {helpText}
        </p>
      ) : null}
      {errorText ? (
        <p id={errorId} role="alert" className="mt-1 text-xs text-red-600 dark:text-red-400">
          {errorText}
        </p>
      ) : null}
    </div>
  );
}
